import type { PaletteMode } from '@mui/material';
import type { PaletteOptions } from '@mui/material/styles';

// ============================================================================
// Core accent colors (theme-independent)
// ============================================================================
export const BRAND = {
  violet: '#7c5cff',
  indigo: '#5b5bf6',
  cyan: '#22c3d1',
  pink: '#ec4899',
} as const;

export const STATUS = {
  success: { main: '#10b981', light: '#6ee7b7', dark: '#047857' },
  warning: { main: '#f59e0b', light: '#fcd34d', dark: '#b45309' },
  error:   { main: '#ef4444', light: '#fca5a5', dark: '#b91c1c' },
  info:    { main: '#3b82f6', light: '#93c5fd', dark: '#1d4ed8' },
} as const;

// ============================================================================
// Light palette
// ============================================================================
const baseLightBackgrounds = {
  bg:        '#f6f7fb',
  surface:   '#ffffff',
  subtle:    '#eef0f6',
  elevated:  '#ffffff',
};

export const lightPalette: PaletteOptions = {
  mode: 'light',
  tonalOffset: 0.15,
  contrastThreshold: 3,
  primary: {
    main: BRAND.indigo,
    light: '#8b8bff',
    dark: '#3f3fd1',
    contrastText: '#ffffff',
  },
  secondary: {
    main: BRAND.pink,
    light: '#f472b6',
    dark: '#be185d',
    contrastText: '#ffffff',
  },
  success: STATUS.success,
  warning: STATUS.warning,
  error:   STATUS.error,
  info:    STATUS.info,
  background: {
    default: baseLightBackgrounds.bg,
    paper:   baseLightBackgrounds.surface,
  },
  text: {
    primary:   '#0b1020',
    secondary: '#55607a',
    disabled:  '#8591a8',
  },
  divider: 'rgba(15,23,42,0.08)',
  action: {
    active:      'rgba(11,16,32,0.65)',
    hover:       'rgba(11,16,32,0.05)',
    selected:    'rgba(91,91,246,0.08)',
    disabled:    'rgba(11,16,32,0.30)',
    disabledBackground: 'rgba(11,16,32,0.04)',
    focus:       'rgba(91,91,246,0.12)',
  },
};

// ============================================================================
// Dark palette
// ============================================================================
const baseDarkBackgrounds = {
  bg:        '#0a0b14',
  surface:   '#12141f',
  subtle:    '#171a27',
  elevated:  '#141623',
};

export const darkPalette: PaletteOptions = {
  mode: 'dark',
  tonalOffset: 0.2,
  contrastThreshold: 3,
  primary: {
    main: '#8b8bff',
    light: '#b1b1ff',
    dark: '#5b5bf6',
    contrastText: '#0a0b14',
  },
  secondary: {
    main: BRAND.pink,
    light: '#f9a8d4',
    dark: '#be185d',
    contrastText: '#0a0b14',
  },
  success: { main: '#34d399', light: '#6ee7b7', dark: '#065f46' },
  warning: { main: '#fbbf24', light: '#fcd34d', dark: '#92400e' },
  error:   { main: '#f87171', light: '#fca5a5', dark: '#991b1b' },
  info:    { main: '#60a5fa', light: '#93c5fd', dark: '#1e3a8a' },
  background: {
    default: baseDarkBackgrounds.bg,
    paper:   baseDarkBackgrounds.surface,
  },
  text: {
    primary:   '#eceefc',
    secondary: '#a2a8c0',
    disabled:  '#6b718f',
  },
  divider: 'rgba(255,255,255,0.08)',
  action: {
    active:      'rgba(236,238,252,0.85)',
    hover:       'rgba(255,255,255,0.06)',
    selected:    'rgba(139,139,255,0.12)',
    disabled:    'rgba(236,238,252,0.30)',
    disabledBackground: 'rgba(255,255,255,0.04)',
    focus:       'rgba(139,139,255,0.16)',
  },
};

// ============================================================================
// Decorative background palettes for gradient-mesh
// ============================================================================
export interface BackgroundPalette {
  bg: string;
  surface: string;
  subtle: string;
  elevated: string;
  mesh: {
    blob1: string;
    blob2: string;
    blob3: string;
  };
  grid: string;
}

export const backgrounds: Record<PaletteMode, BackgroundPalette> = {
  light: {
    ...baseLightBackgrounds,
    mesh: {
      blob1: 'rgba(91, 91, 246, 0.22)',
      blob2: 'rgba(34, 195, 209, 0.18)',
      blob3: 'rgba(236, 72, 153, 0.14)',
    },
    grid: 'rgba(15,23,42,0.04)',
  },
  dark: {
    ...baseDarkBackgrounds,
    mesh: {
      blob1: 'rgba(139, 139, 255, 0.22)',
      blob2: 'rgba(94, 231, 241, 0.16)',
      blob3: 'rgba(244, 114, 182, 0.12)',
    },
    grid: 'rgba(255,255,255,0.035)',
  },
};
